<script setup lang="ts">
import { toTypedSchema } from '@vee-validate/zod'
import { useForm } from 'vee-validate'
import { onMounted } from 'vue'

import type { Address } from '@/types/external/2work/doctypes/generatorOutput/Contacts/Address.ts'
import type { AddressCreation } from '@/types/external/2work/doctypes/generatorOutput/Contacts/AddressCreation.ts'
import type { DynamicLink } from '@/types/external/2work/doctypes/generatorOutput/Core/DynamicLink.ts'
import type { FrappeInterceptNewCreateMsgPayload } from '@/utilities/handleFrappeInterceptNewCreateActions.ts'

import ButtonPrimary from '@/buttons/ButtonPrimary.vue'
import { useZodSchemaStore } from '@/components/editRelationGroup/sectionSelfUpdates/zodSchemaStore.ts'
import { useNetworkError } from '@/composables/useNetworkError.ts'
import { useReportIframeStore } from '@/iframeStores/reportIframeStore.ts'
import { useFrappeNestCustomAPIStore } from '@/stores/apis/frappeNestCustomAPI'
import { useModalManagerStore } from '@/stores/modalManager'
import { useSimpleNotificationStore } from '@/stores/simpleNotificationStore.ts'
import { translationFunctionDefault } from '@/utilities/translations.ts'

import SingleAddressEditor from './SingleAddressEditor.vue'

const { frappeInterceptNewCreateMsgPayload } = defineProps<{
  frappeInterceptNewCreateMsgPayload: FrappeInterceptNewCreateMsgPayload
}>()

// translations
const tfDefault = translationFunctionDefault()

// stores
const simpleNotificationStore = useSimpleNotificationStore()
const modalManager = useModalManagerStore()
const frappeCustom = useFrappeNestCustomAPIStore()
const reportIframeStore = useReportIframeStore()

const onError = (): void => {
  simpleNotificationStore.triggerSimpleNotificationError(
    tfDefault('frappeInterceptNewCreateActionMap.addressCreation.errorMsg'),
  )
}
const handleNetworkError = useNetworkError(onError)

const { clickedFieldName, parentDoctype, targetName } = frappeInterceptNewCreateMsgPayload

// eslint-disable-next-line func-style, complexity
function resolveAddressLink(parent: typeof parentDoctype, field: string): DynamicLink | null {
  if (parent) {
    // Quotation: quotation_to + party_name
    if (parent.quotation_to && parent.party_name) {
      return {
        doctype: 'Dynamic Link',
        link_doctype: parent.quotation_to,
        link_name: parent.party_name,
      }
    }
    // Buying
    if (
      (parent.doctype === 'Request for Quotation' ||
        parent.doctype === 'Supplier Quotation' ||
        parent.doctype === 'Purchase Order') &&
      field === 'billing_address' &&
      parent.company
    ) {
      return {
        doctype: 'Dynamic Link',
        link_doctype: 'Company',
        link_name: parent.company,
      }
    }
    if (
      parent.doctype === 'Supplier Quotation' &&
      field === 'supplier_address' &&
      parent.supplier
    ) {
      return {
        doctype: 'Dynamic Link',
        link_doctype: 'Supplier',
        link_name: parent.supplier,
      }
    }
    if (parent.doctype === 'Supplier Quotation' && field === 'shipping_address' && parent.company) {
      return {
        doctype: 'Dynamic Link',
        link_doctype: 'Company',
        link_name: parent.company,
      }
    }
    if (
      parent.doctype === 'Purchase Order' &&
      field === 'shipping_address' &&
      parent.tw_is_direct_delivery_to_customer === 1 &&
      parent.tw_direct_delivery_customer
    ) {
      return {
        doctype: 'Dynamic Link',
        link_doctype: 'Customer',
        link_name: parent.tw_direct_delivery_customer,
      }
    }
    if (
      parent.doctype === 'Purchase Order' &&
      field === 'shipping_address' &&
      parent.tw_is_direct_delivery_to_customer === 0 &&
      parent.company
    ) {
      return {
        doctype: 'Dynamic Link',
        link_doctype: 'Company',
        link_name: parent.company,
      }
    }
    // Stock
    if (
      parent.doctype === 'Purchase Receipt' &&
      (field === 'shipping_address' || field === 'billing_address') &&
      parent.company
    ) {
      return {
        doctype: 'Dynamic Link',
        link_doctype: 'Company',
        link_name: parent.company,
      }
    }
    // Sales
    if (parent.customer)
      return { doctype: 'Dynamic Link', link_doctype: 'Customer', link_name: parent.customer }
    if (parent.lead)
      return { doctype: 'Dynamic Link', link_doctype: 'Lead', link_name: parent.lead }
    if (parent.opportunity)
      return { doctype: 'Dynamic Link', link_doctype: 'Opportunity', link_name: parent.opportunity }
    // Purchase
    if (parent.supplier)
      return { doctype: 'Dynamic Link', link_doctype: 'Supplier', link_name: parent.supplier }
    // generic Party
    if (parent.party_type && parent.party_name) {
      return {
        doctype: 'Dynamic Link',
        link_doctype: parent.party_type,
        link_name: parent.party_name,
      }
    }
    // Firm address
    if (parent.company)
      return { doctype: 'Dynamic Link', link_doctype: 'Company', link_name: parent.company }

    if (parent.doctype === 'Company')
      return { doctype: 'Dynamic Link', link_doctype: 'Company', link_name: parent.name }
  }

  return null
}

const resolvedAddressType: AddressCreation['address_type'] =
  clickedFieldName === 'shipping_address_name' ? 'Shipping' : 'Billing'

const {
  relationSchemas: { singleAddressSchema },
} = useZodSchemaStore()

type AddressFormValues = typeof singleAddressSchema._type
const validationSchema = toTypedSchema(singleAddressSchema)
const { handleSubmit, setValues } = useForm<AddressFormValues>({
  validationSchema,
})

const updateAddressTiggerList = ['edit_address']
const onSubmit = async (values: AddressFormValues): Promise<void> => {
  try {
    const link = resolveAddressLink(parentDoctype, clickedFieldName)
    if (!link) {
      console.error('Could not resolve link for parent doctype:', parentDoctype)
      await handleNetworkError(new Error('Could not resolve link for parent doctype'))
      return
    }

    let is_your_company_address: 0 | 1 = 0
    if (
      parentDoctype?.doctype === 'Company' ||
      (link.link_doctype === 'Company' && link.link_name === parentDoctype?.company)
    ) {
      is_your_company_address = 1
    }
    const is_primary_address: 0 | 1 = values.is_primary_address ? 1 : 0
    const payload = {
      address_line1: values.address_line1,
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      address_line2: values.address_line2 ?? undefined,
      address_title: values.address_title,
      address_type: resolvedAddressType,
      city: values.city,
      country: values.country,
      is_primary_address,
      is_your_company_address,
      links: [link],
      pincode: values.pincode,
      state: values.state ?? '',
    }

    if (
      targetName &&
      updateAddressTiggerList.includes(frappeInterceptNewCreateMsgPayload.clickedFieldName)
    ) {
      const updateAddress: Partial<Address> = payload
      await frappeCustom.updateAddress(targetName, updateAddress).then(() => {
        if (parentDoctype?.doctype === 'Company') {
          reportIframeStore.triggerIframeAction('reloadDoc', {})
        }
        modalManager.closeModal()
        simpleNotificationStore.triggerSimpleNotificationSuccess(
          tfDefault('frappeInterceptNewCreateActionMap.addressCreation.successMsg'),
        )
      })
    } else {
      const createAddress: AddressCreation = {
        ...payload,
        doctype: 'Address',
      }
      await frappeCustom.createAddress(createAddress).then(() => {
        if (parentDoctype?.doctype === 'Company') {
          reportIframeStore.triggerIframeAction('reloadDoc', {})
        }
        modalManager.closeModal()
        simpleNotificationStore.triggerSimpleNotificationSuccess(
          tfDefault('frappeInterceptNewCreateActionMap.addressCreation.successMsg'),
        )
      })
    }
  } catch (submitError) {
    console.error('Error submitting form:', submitError)
    await handleNetworkError(submitError)
  }
}

const onSubmitHandler = handleSubmit(async (values) => {
  await onSubmit(values)
})

const prefillAddressValues = async (): Promise<void> => {
  if (!updateAddressTiggerList.includes(frappeInterceptNewCreateMsgPayload.clickedFieldName)) {
    return
  }

  if (!targetName) {
    return
  }

  try {
    const address = await frappeCustom.getAddress(targetName)

    setValues({
      address_line1: address.address_line1 ?? '',
      address_line2: address.address_line2 ?? '',
      address_title: address.address_title ?? '',
      address_type: address.address_type ?? 'Other',
      city: address.city ?? '',
      country: address.country ?? 'Germany',
      doctype: 'Address',
      is_primary_address: address.is_primary_address ?? 0,
      name: address.name,
      pincode: address.pincode ?? '',
      state: address.state ?? '',
      tw_alternative_recipient: address.tw_alternative_recipient ?? '',
    })
  } catch (errorMsg) {
    console.error('getBankAccountList error', errorMsg)
    await handleNetworkError(errorMsg)
  }
}

// hooks
onMounted(() => {
  prefillAddressValues().catch((errorMsg: unknown) => {
    console.error('prefillBankAccountValues error', errorMsg)
  })
})
</script>

<template>
  <form @submit="onSubmitHandler">
    <div class="py-4">
      <SingleAddressEditor :with-primary="parentDoctype?.doctype === 'Company'" />
    </div>
    <div class="flex justify-end">
      <ButtonPrimary label="Speichern" type="submit" />
    </div>
  </form>
</template>
